import { Trash2, ExternalLink, GripVertical } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScheduledActivityResponse } from '../../types/scheduledActivity';
import { Button } from '../ui/button';

interface ScheduledActivityCardProps {
  scheduledActivity: ScheduledActivityResponse;
  onDelete: (scheduledActivity: ScheduledActivityResponse) => void;
  isDeleting?: boolean;
  dragHandleProps?: Record<string, unknown>;
  viewOnly?: boolean;
}

export function ScheduledActivityCard({
  scheduledActivity,
  onDelete,
  isDeleting = false,
  dragHandleProps,
  viewOnly,
}: ScheduledActivityCardProps) {
  return (
    <div className="p-2 bg-white rounded border border-gray-200 shadow-sm hover:shadow transition-shadow flex items-center gap-2">
      {/* Drag handle — hidden in view-only */}
      {dragHandleProps && !viewOnly && (
        <button
          {...dragHandleProps}
          className="cursor-grab active:cursor-grabbing touch-none p-1 text-gray-400 hover:text-gray-600"
          title="Drag to reorder"
        >
          <GripVertical className="h-3 w-3" />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{scheduledActivity.activity.title}</p>
        {/* Age range badge */}
        {scheduledActivity.activity.ageRanges.length > 0 && (
          <div className="flex gap-1 mt-0.5">
            {scheduledActivity.activity.ageRanges.map(ar => (
              <span
                key={`${ar.fromAge}-${ar.toAge}`}
                className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full"
              >
                {ar.fromAge} – {ar.toAge}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Delete button — hidden in view-only */}
        {!viewOnly && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => onDelete(scheduledActivity)}
            disabled={isDeleting}
            title="Remove from schedule"
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        )}
        <Link to={`/admin/activities/${scheduledActivity.activityId}`}>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            title="View activity details"
          >
            <ExternalLink className="h-4 w-4 text-gray-600" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
